import { useState } from "react";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { ImageSlot, MediaPicker } from "../media/MediaPicker";
import { GuideLabelInput } from "./GuideLabelInput";
import { useSetOptionGuide, type OptionGuideVariables } from "./useGiftOptions";

const GUIDE_HELP =
	"An image shoppers open from this gift's details, below its description. Usually a size guide, but it can be a care guide, what's in the box, or anything a product page would show. Uploads are converted to WebP.";

type OptionGuideFieldProps = {
	campaignId: string;
	giftId: string;
	optionId: string;
	guideLabel: string | null;
	guideAssetId: string | null;
};

export const OptionGuideField = ({
	campaignId,
	giftId,
	optionId,
	guideLabel,
	guideAssetId,
}: OptionGuideFieldProps) => {
	const setGuide = useSetOptionGuide(campaignId, giftId);
	const toast = useToast();
	const [picking, setPicking] = useState(false);

	const save = (patch: Omit<OptionGuideVariables, "optionId">, done: string) =>
		setGuide.mutate(
			{ optionId, ...patch },
			{
				onSuccess: () => toast.push(done),
				onError: (error) => toast.push(error.message, "error"),
			},
		);

	return (
		<div className="space-y-2">
			<LabelWithHint label="Guide image">{GUIDE_HELP}</LabelWithHint>
			<div className="grid gap-3 sm:grid-cols-2">
				<ImageSlot
					assetId={guideAssetId}
					label="Image"
					onPick={() => setPicking(true)}
					onClear={guideAssetId ? () => save({ guideAssetId: null }, "Guide removed") : undefined}
				/>
				{guideAssetId ? (
					<GuideLabelInput
						key={guideLabel ?? ""}
						guideLabel={guideLabel}
						onCommit={(next) => save({ guideLabel: next }, "Saved")}
					/>
				) : null}
			</div>

			<MediaPicker
				open={picking}
				onOpenChange={setPicking}
				onSelect={(asset) => {
					setPicking(false);
					save({ guideAssetId: asset.id }, "Guide added");
				}}
			/>
		</div>
	);
};
